// Libraries
import { formatInTimeZone } from 'date-fns-tz';
// Types
import { LineTooltipRatingProps } from './LineTooltipRating.types';
import { TooltipDecorator, TooltipDecoratorTypesEnum } from '@molecules/index';
// Utils
import { formatValue, findRangeKey } from '@helpers/index';
import { Rating } from '@utils/constants';

/**
 * LineTooltipWrapper is a component that wraps a Victory bar tooltip with custom content.
 *
 * @component
 * @param {LineTooltipRatingProps} props - Props for the BarTooltipWrapper component.
 * @param {RatingPoint} props.datum - The data point object for which the tooltip is being rendered.
 * @param {number} [props.x=0] - The x-coordinate of the tooltip position.
 * @param {number} [props.y=0] - The y-coordinate of the tooltip position.
 * @returns {ReactElement} A component that renders a custom tooltip for a Victory bar.
 */
export const LineTooltipRating = ({ x = 0, y = 0, datum, ratingRanges }: LineTooltipRatingProps) => {
  // Destructure properties from the datum object, the assertion comes from the
  // the non type model of victory to use {datum}.
  const { x: xValue = 0, y: yValue = 0, title = '' } = datum ?? {};
  const rangeKey = ratingRanges && findRangeKey(Number(yValue), ratingRanges);
  const xAxisCorrection = x - 45;
  const yAxisCorrection = y - 10;
  const rating = rangeKey ?? Rating.A;

  return (
    <TooltipDecorator
      type={TooltipDecoratorTypesEnum.RatingTextTooltip}
      positionObject={{
        x: xAxisCorrection,
        y: yAxisCorrection,
      }}
      title={title}
      params={{
        rating,
        text: formatInTimeZone(new Date(xValue), 'UTC', 'MMM d'),
        description: formatValue(Number(yValue), 2),
      }}
    />
  );
};
